import { useEffect, useState } from "react";
import { Download, Smartphone, X } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * Persistent "Install app" card for the doctor's Profile and the patient's Portal.
 * - On Chrome/Edge/Android: uses `window.__deferredInstallPrompt` captured by
 *   InstallPrompt (or a fresh `beforeinstallprompt` event) to trigger install.
 * - On iOS Safari: shows step-by-step instructions to Add to Home Screen.
 * - Hides itself when the app is already running as an installed PWA.
 */
export default function InstallAppButton() {
  const { t } = useTranslation();
  const [canInstall, setCanInstall] = useState(!!window.__deferredInstallPrompt);
  const [installed, setInstalled] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);

  useEffect(() => {
    const standalone = window.matchMedia("(display-mode: standalone)").matches
      || window.navigator.standalone === true
      || localStorage.getItem("pwa_installed") === "1";
    if (standalone) { setInstalled(true); return; }
    const onAvailable = () => setCanInstall(true);
    const onBefore = (e) => { e.preventDefault(); window.__deferredInstallPrompt = e; setCanInstall(true); };
    const onInstalled = () => { localStorage.setItem("pwa_installed", "1"); setInstalled(true); };
    window.addEventListener("pwa-prompt-available", onAvailable);
    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("pwa-prompt-available", onAvailable);
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;

  const ua = navigator.userAgent;
  const isIos = /iphone|ipad|ipod/i.test(ua);

  async function install() {
    const evt = window.__deferredInstallPrompt;
    if (!evt) { setShowIosHelp(true); return; }
    evt.prompt();
    const { outcome } = await evt.userChoice;
    if (outcome === "accepted") setInstalled(true);
    window.__deferredInstallPrompt = null;
    setCanInstall(false);
  }

  return (
    <>
      <div data-testid="install-app-card"
        className="bg-white border border-[#D4A373]/40 rounded-2xl p-5 flex items-start gap-4">
        <div className="w-11 h-11 rounded-xl bg-[#D4A373]/20 flex items-center justify-center flex-shrink-0">
          <Smartphone className="w-5 h-5 text-[#B07D4F]" strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading text-lg font-light">{t("install.cardTitle")}</div>
          <p className="text-xs text-stone-500 mt-1 leading-relaxed">{t("install.cardSubtitle")}</p>
          <button onClick={() => (canInstall ? install() : (isIos ? setShowIosHelp(true) : install()))}
            data-testid="install-app-btn"
            className="mt-3 inline-flex items-center gap-2 px-5 py-2 rounded-full bg-stone-900 text-white text-xs uppercase tracking-widest hover:bg-stone-700 transition-colors">
            <Download className="w-3.5 h-3.5" /> {t("install.cardCta")}
          </button>
        </div>
      </div>

      {showIosHelp && (
        <div data-testid="install-ios-modal"
          className="fixed inset-0 z-50 bg-black/60 flex items-end md:items-center justify-center p-4"
          onClick={() => setShowIosHelp(false)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div className="font-heading text-2xl font-light">{t("install.iosStepsTitle")}</div>
              <button onClick={() => setShowIosHelp(false)} className="text-stone-400 hover:text-stone-900"><X className="w-4 h-4" /></button>
            </div>
            <ol className="space-y-3 text-sm text-stone-700 list-decimal pl-5">
              <li>{t("install.iosStep1")}</li>
              <li>{t("install.iosStep2")}</li>
              <li>{t("install.iosStep3")}</li>
            </ol>
            <p className="mt-4 text-xs text-stone-500">{t("install.iosNote")}</p>
          </div>
        </div>
      )}
    </>
  );
}
